import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import {
  Lock, Info, Edit3, Check, X, Loader2, Mail, Phone, AlertTriangle,
} from 'lucide-react';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return iso; }
};

const PHONE_RE = /^[\d\s\-().+]{7,}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Personal Information — read-only fields sourced from agency payroll,
 * plus two participant-editable fields (email, phone).
 *
 * Payroll-owned fields cannot be edited here.
 * Editable fields: email, phone (PUT /api/participant/profile).
 */
export const PersonalInformationPanel = ({ profile, onProfileUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState(profile?.email || '');
  const [phone, setPhone] = useState(profile?.phone || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  useEffect(() => {
    setEmail(profile?.email || '');
    setPhone(profile?.phone || '');
  }, [profile?.email, profile?.phone]);

  if (!profile) return null;

  const readOnlyFields = [
    { label: 'Full Legal Name',                value: profile.name,                testId: 'field-full-name' },
    { label: 'Employee ID',                    value: profile.employeeId,          testId: 'field-employee-id', mono: true },
    { label: 'Agency',                         value: profile.agencyName || profile.agency, testId: 'field-agency' },
    { label: 'Title',                          value: profile.title || '—',        testId: 'field-title' },
    { label: 'Pay Grade',                      value: profile.payGrade || '—',     testId: 'field-pay-grade', mono: true },
    { label: 'Retirement System (from Agency Payroll)',
                                               value: profile.retirementSystem,    testId: 'field-retirement-system', badge: true },
    { label: 'Date of Birth',                  value: fmtDate(profile.dateOfBirth), testId: 'field-dob' },
    { label: 'Service Entry Date',             value: fmtDate(profile.serviceEntryDate || profile.hireDate), testId: 'field-service-entry' },
    { label: 'Retirement Account Number',
                                               value: profile.tspAccountMasked || (profile.tspAccountLastFour ? `••••••••${profile.tspAccountLastFour}` : '—'),
                                               testId: 'field-tsp-account', mono: true },
  ];

  const handleCancel = () => {
    setEmail(profile.email || '');
    setPhone(profile.phone || '');
    setErr('');
    setEditing(false);
  };

  const validate = () => {
    if (email && !EMAIL_RE.test(email)) return 'Please enter a valid email address.';
    if (phone && !PHONE_RE.test(phone)) return 'Phone must contain digits and may include +, -, (), spaces.';
    return '';
  };

  const handleSave = async () => {
    setErr(''); setOk('');
    const v = validate();
    if (v) { setErr(v); return; }
    setSaving(true);
    try {
      const body = {};
      if (email !== profile.email) body.email = email;
      if (phone !== profile.phone) body.phone = phone;
      if (Object.keys(body).length === 0) {
        setEditing(false); setSaving(false); return;
      }
      await api.put('/participant/profile', body);
      setEditing(false);
      setOk('Saved.');
      onProfileUpdate?.();
      setTimeout(() => setOk(''), 2500);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(Array.isArray(detail) ? detail[0]?.msg || 'Update failed.' : detail || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm text-[#1B2A4A] ' +
    'focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20';

  return (
    <div className="panel p-0 overflow-hidden" data-testid="personal-information-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E5E9] bg-[#FAFBFC]">
        <div>
          <h2 className="text-sm font-semibold text-[#1B2A4A] uppercase tracking-wider">
            Personal Information
          </h2>
          <p className="text-[11px] text-[#6B7280] mt-0.5">
            Agency payroll is the system of record for name, title, grade, and service dates.
          </p>
        </div>
        <span
          className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-wider px-2 py-1 rounded bg-gray-100 border border-[#D1D5DB] text-[#6B7280]"
          data-testid="readonly-badge"
        >
          <Lock size={10} /> PAYROLL-SOURCED
        </span>
      </div>

      {/* Info notice */}
      <div className="px-5 py-3 bg-blue-50 border-b border-blue-200">
        <div className="flex items-start gap-2">
          <Info size={13} className="text-blue-600 mt-0.5 shrink-0" />
          <p className="text-[11px] text-blue-700 leading-relaxed">
            Payroll-sourced fields below cannot be edited here. To correct them, contact your
            agency Human Resources or Payroll Office. You may update your email and phone
            number anytime.
          </p>
        </div>
      </div>

      {/* Read-only field grid */}
      <dl className="grid grid-cols-1 md:grid-cols-2">
        {readOnlyFields.map((f, idx) => (
          <div
            key={f.testId}
            className={
              'px-5 py-4 border-[#F0F1F3] border-b ' +
              (idx % 2 === 0 ? 'md:border-r ' : '')
            }
          >
            <dt className="text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">
              {f.label}
            </dt>
            {f.badge ? (
              <dd>
                <span
                  className="inline-block font-mono font-semibold text-xs px-2 py-1 rounded bg-[#1B2A4A] text-white"
                  data-testid={f.testId}
                >
                  {f.value || '—'}
                </span>
              </dd>
            ) : (
              <dd
                className={
                  'text-sm text-[#1B2A4A] ' +
                  (f.mono ? 'font-mono font-medium' : 'font-medium')
                }
                data-testid={f.testId}
              >
                {f.value || '—'}
              </dd>
            )}
          </div>
        ))}
      </dl>

      {/* Editable fields: Contact */}
      <div className="border-t-2 border-[#E2E5E9]" data-testid="contact-section">
        <div className="flex items-center justify-between px-5 py-3 bg-[#FAFBFC] border-b border-[#E2E5E9]">
          <div>
            <h3 className="text-xs font-semibold text-[#1B2A4A] uppercase tracking-wider">
              Contact Information
            </h3>
            <p className="text-[11px] text-[#6B7280] mt-0.5">
              Participant-editable. Used for account alerts and statement delivery.
            </p>
          </div>
          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              data-testid="edit-contact-btn"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded text-[11px] font-medium text-[#1B2A4A] border border-[#D1D5DB] hover:border-[#1B2A4A] hover:bg-gray-50 transition-colors"
            >
              <Edit3 size={11} /> Edit
            </button>
          ) : (
            <div className="flex items-center gap-1.5">
              <button
                onClick={handleCancel}
                disabled={saving}
                data-testid="cancel-contact-btn"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded text-[11px] font-medium text-[#6B7280] border border-[#D1D5DB] hover:bg-gray-50 disabled:opacity-50"
              >
                <X size={11} /> Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-contact-btn"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded text-[11px] font-medium text-white bg-[#1B2A4A] hover:bg-[#0F1A33] disabled:opacity-50"
              >
                {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                Save
              </button>
            </div>
          )}
        </div>

        {(err || ok) && (
          <div className="px-5 py-2">
            {err && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="contact-error">
                <AlertTriangle size={12} className="text-red-600" />
                <span className="text-xs text-red-700">{err}</span>
              </div>
            )}
            {ok && (
              <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="contact-success">
                <Check size={12} className="text-emerald-600" />
                <span className="text-xs text-emerald-700">{ok}</span>
              </div>
            )}
          </div>
        )}

        <dl className="grid grid-cols-1 md:grid-cols-2">
          <div className="px-5 py-4 border-b md:border-r md:border-b-0 border-[#F0F1F3]">
            <dt className="flex items-center gap-1.5 text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">
              <Mail size={10} /> Email Address
            </dt>
            <dd>
              {editing ? (
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className={inputClass}
                  placeholder="name@agency.gov"
                  data-testid="input-email"
                />
              ) : (
                <span className="text-sm font-medium text-[#1B2A4A]" data-testid="field-email">
                  {profile.email || '—'}
                </span>
              )}
            </dd>
          </div>
          <div className="px-5 py-4">
            <dt className="flex items-center gap-1.5 text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">
              <Phone size={10} /> Phone Number
            </dt>
            <dd>
              {editing ? (
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  className={inputClass}
                  placeholder="+1 (202) 555-0100"
                  data-testid="input-phone"
                />
              ) : (
                <span className="text-sm font-medium text-[#1B2A4A] font-mono" data-testid="field-phone">
                  {profile.phone || '—'}
                </span>
              )}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
};

export default PersonalInformationPanel;
